import BaseService from '@/services/BaseService';

/**
 * Service configuration for registering services dynamically
 */
export interface ServiceConfig {
  name: string;
  baseUrl?: string;
  path?: string;
  factory?: () => BaseService | any;
}

/**
 * Service registry that manages all registered services
 */
export class ServiceRegistry {
  private services: Map<string, BaseService | any> = new Map();
  private configs: Map<string, ServiceConfig> = new Map();

  /**
   * Register a service configuration
   */
  registerService(config: ServiceConfig): void {
    this.configs.set(config.name, config);

    // If a factory is provided, create the service immediately
    if (config.factory) {
      const service = config.factory();
      this.services.set(config.name, service);
    } else if (config.baseUrl) {
      // Create a default BaseService if only baseUrl is provided
      const service = new BaseService(config.baseUrl);
      this.services.set(config.name, service);
    }
  }

  /**
   * Register multiple services at once
   */
  registerServices(configs: ServiceConfig[]): void {
    configs.forEach(config => this.registerService(config));
  }

  /**
   * Get a service by name
   */
  getService<T = any>(name: string): T | undefined {
    return this.services.get(name) as T;
  }

  /**
   * Get all services as an object
   */
  getAllServices(): Record<string, any> {
    const result: Record<string, any> = {};
    this.services.forEach((service, name) => {
      result[name] = service;
    });
    return result;
  }

  /**
   * Check if a service is registered
   */
  hasService(name: string): boolean {
    return this.services.has(name);
  }

  /**
   * Clear all services (useful for testing)
   */
  clear(): void {
    this.services.clear();
    this.configs.clear();
  }
}

// Create a singleton instance
export const serviceRegistry = new ServiceRegistry();
